<script lang="ts">
  /**
   * Die zwei Fragen der Reiseart — „Womit fährst du?" und „Wo schläfst du?".
   *
   * ## Warum kein Formular
   *
   * Es sind zwei Fragen, keine Konfiguration. Mit Auswahlfeldern, Untermenüs
   * und Vorgabewerten sähe die Stelle nach einer Einrichtung aus, durch die
   * man sich arbeiten muss — dabei ist beides mit einem Blick beantwortet und
   * später änderbar.
   *
   * Jede Antwort trägt **einen Satz, was sie ändert.** Ohne ihn ist es Raten:
   * „Elektro" klingt nach einer Angabe über das Auto, ist aber eine
   * Entscheidung über die halbe Oberfläche. Die Sätze stehen in `reiseart.ts`,
   * weil sie hier und in den Reiseeinstellungen dieselbe Frage beantworten.
   *
   * ## Zwei Fassungen, eine Wahrheit
   *
   * | `fassung` | Wo | Warum |
   * |---|---|---|
   * | `gross` | beim Anlegen | Der wichtigste Moment: große Ziele mit Zeichen, **alle** Folgesätze sichtbar, nichts vorgewählt |
   * | `knapp` | Reiseeinstellungen | Dort wird korrigiert, nicht entschieden — zwei Schalterpaare und der Satz zur gewählten Antwort |
   *
   * Ein Bauteil und nicht zwei, damit die Fragen, die Beschriftungen und die
   * Zeichen nicht an zwei Stellen gepflegt werden müssen — genau daran laufen
   * solche Paare auseinander.
   *
   * ## Anders als bisher: beim Anlegen ist nichts vorgewählt
   *
   * Vorher stand die Vorgabe (Elektro, im Fahrzeug) schon gewählt da, damit
   * niemand die Frage bemerken muss. Der Entwurf dreht das um, und mit Recht:
   * Wer sie nicht bemerkt, sucht später den Reiter „Laden" in einer Reise, in
   * der es ihn nicht gibt. Zwei Klicks sind der Preis dafür, dass die
   * Entscheidung eine ist. In den Einstellungen bleibt es umgekehrt — dort
   * steht immer eine Antwort, weil die Reise schon eine hat.
   *
   * Die Zeichen kommen aus `gestaltung/muster/muster.ts`: Dort stehen sie für
   * die `Reiseartmarke`, und zwei Wahrheiten über dieselben zwei Achsen wären
   * eine zu viel.
   */
  import Zeichen from '../gestaltung/Zeichen.svelte';
  import { ANTRIEB_MARKE, NACHT_MARKE, type Achsenteil } from '../gestaltung/muster/muster';
  import {
    ANTRIEB_FRAGE,
    ANTRIEB_WAHL,
    UEBERNACHTUNG_FRAGE,
    UEBERNACHTUNG_WAHL,
    type Antrieb,
    type Uebernachtung
  } from './reiseart';

  interface Props {
    /** `null` heißt: noch nicht beantwortet. Nur in der großen Fassung möglich. */
    antrieb: Antrieb | null;
    uebernachtung: Uebernachtung | null;
    onAntrieb: (a: Antrieb) => void;
    onUebernachtung: (u: Uebernachtung) => void;
    fassung?: 'gross' | 'knapp';
  }
  let { antrieb, uebernachtung, onAntrieb, onUebernachtung, fassung = 'knapp' }: Props =
    $props();

  const gross = $derived(fassung === 'gross');

  const antriebFolge = $derived(ANTRIEB_WAHL.find((w) => w.wert === antrieb)?.folge ?? '');
  const uebernachtungFolge = $derived(
    UEBERNACHTUNG_WAHL.find((w) => w.wert === uebernachtung)?.folge ?? ''
  );
</script>

<!-- Ein Ziel in der großen Fassung: Zeichen links, Wort und Folgesatz rechts.
     Die Trefferfläche ist der ganze Kasten und nicht nur das Wort. -->
{#snippet ziel(
  an: boolean,
  klick: () => void,
  zeichen: Achsenteil['zeichen'],
  wort: string,
  folge: string
)}
  <button type="button" role="radio" aria-checked={an} class="ziel" class:an onclick={klick}>
    <span class="symbol"><Zeichen name={zeichen} groesse={22} /></span>
    <span class="worte">
      <strong>{wort}</strong>
      <span class="folge">{folge}</span>
    </span>
  </button>
{/snippet}

{#if gross}
  <div class="reiseart gross">
    <fieldset>
      <legend><span class="nr" data-zahl>1</span>{ANTRIEB_FRAGE}</legend>
      <div class="ziele" role="radiogroup" aria-label={ANTRIEB_FRAGE}>
        {#each ANTRIEB_WAHL as w (w.wert)}
          {@render ziel(
            antrieb === w.wert,
            () => onAntrieb(w.wert),
            ANTRIEB_MARKE[w.wert].zeichen,
            w.label,
            w.folge
          )}
        {/each}
      </div>
    </fieldset>

    <fieldset>
      <legend><span class="nr" data-zahl>2</span>{UEBERNACHTUNG_FRAGE}</legend>
      <div class="ziele" role="radiogroup" aria-label={UEBERNACHTUNG_FRAGE}>
        {#each UEBERNACHTUNG_WAHL as w (w.wert)}
          {@render ziel(
            uebernachtung === w.wert,
            () => onUebernachtung(w.wert),
            NACHT_MARKE[w.wert].zeichen,
            w.label,
            w.folge
          )}
        {/each}
      </div>
    </fieldset>
  </div>
{:else}
  <div class="reiseart">
    <div class="zeile">
      <span class="frage" id="frage-antrieb">{ANTRIEB_FRAGE}</span>
      <div class="knoepfe" role="radiogroup" aria-labelledby="frage-antrieb">
        {#each ANTRIEB_WAHL as w (w.wert)}
          <button
            type="button"
            role="radio"
            aria-checked={antrieb === w.wert}
            class:an={antrieb === w.wert}
            onclick={() => onAntrieb(w.wert)}>{w.label}</button
          >
        {/each}
      </div>
      <p class="folge">{antriebFolge}</p>
    </div>

    <div class="zeile">
      <span class="frage" id="frage-uebernachtung">{UEBERNACHTUNG_FRAGE}</span>
      <div class="knoepfe" role="radiogroup" aria-labelledby="frage-uebernachtung">
        {#each UEBERNACHTUNG_WAHL as w (w.wert)}
          <button
            type="button"
            role="radio"
            aria-checked={uebernachtung === w.wert}
            class:an={uebernachtung === w.wert}
            onclick={() => onUebernachtung(w.wert)}>{w.label}</button
          >
        {/each}
      </div>
      <p class="folge">{uebernachtungFolge}</p>
    </div>
  </div>
{/if}

<style>
  .reiseart {
    display: flex;
    flex-direction: column;
    gap: var(--raum-6);
  }
  .reiseart.gross {
    gap: var(--gruppe-weit);
  }

  /* ── Große Fassung ─────────────────────────────────────────────── */

  fieldset {
    border: 0;
    margin: 0;
    padding: 0;
    display: grid;
    gap: var(--raum-6);
  }

  legend {
    padding: 0;
    font-size: var(--fs-titel);
    font-weight: var(--gw-halb);
  }
  .nr {
    color: var(--text-schwach);
    font-weight: var(--gw-normal);
    margin-right: var(--raum-4);
  }

  /* Nebeneinander, **sobald** Platz ist — dasselbe Raster wie im
     Formularabschnitt. `min(100%, …)` lässt die Spalte auch in einer schmalen
     Leiste schrumpfen, statt sie herauszuschieben. */
  .ziele {
    display: grid;
    gap: var(--raum-5);
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 15rem), 1fr));
  }

  .ziel {
    display: flex;
    gap: var(--raum-6);
    align-items: flex-start;
    text-align: left;
    padding: var(--raum-7);
    min-height: var(--tippziel);
    border: 1px solid var(--border);
    background: var(--surface);
    border-radius: var(--radius);
    box-shadow: var(--schatten-1);
    color: var(--text);
  }
  .ziel.an {
    border-color: var(--accent);
    background: var(--accent-weich);
    box-shadow: none;
  }
  .ziel:hover:not(.an) {
    border-color: var(--border-stark);
  }

  .symbol {
    flex: none;
    margin-top: 2px;
    color: var(--text-dim);
  }
  .ziel.an .symbol {
    color: var(--accent);
  }

  .worte {
    display: grid;
    gap: 2px;
    min-width: 0;
  }
  .worte strong {
    font-size: var(--fs-basis);
    font-weight: var(--gw-halb);
  }
  .worte .folge {
    font-size: var(--fs-fein);
    color: var(--text-dim);
    line-height: var(--zh-normal);
    text-wrap: pretty;
  }

  /* ── Knappe Fassung (Reiseeinstellungen) ───────────────────────── */

  .zeile {
    display: grid;
    /* Frage links, Knöpfe rechts daneben, Folgesatz darunter über die volle
       Breite.

       Die Fragespalte hat eine gemeinsame Mindestbreite und darf nicht
       umbrechen: Jede Zeile ist ihr eigenes Raster, und mit `auto` bekam
       „Womit fährst du?" eine andere Spaltenbreite als „Wo schläfst du?" —
       die Knöpfe der beiden Zeilen standen versetzt, und die zweite Frage
       brach mitten im Satz um. Beide Fragen sind gleich lang gebaut; die
       Mindestbreite hält die Flucht. */
    grid-template-columns: minmax(8.5rem, max-content) 1fr;
    align-items: center;
    gap: var(--raum-3) var(--raum-6);
  }
  .frage {
    font-size: var(--fs-zeile);
    font-weight: var(--gw-halb);
  }
  .knoepfe {
    display: flex;
    /* Umbruch, seit die Übernachtungsachse drei Antworten hat: Nebeneinander
       brauchen „Im Fahrzeug | Gemischt | Unterkunft" rund 290 px, die
       Planerleiste lässt der Spalte aber nur etwa 200 — die dritte Antwort
       stand vorher außerhalb der Leiste und war weder zu sehen noch zu
       treffen. Umbrechen darf hier nur die Knopfgruppe; die Fragespalte
       daneben behält ihre Mindestbreite, damit die Flucht der beiden Zeilen
       hält. */
    flex-wrap: wrap;
    gap: 2px;
    padding: 3px;
    border-radius: var(--radius-rund);
    background: var(--surface-2);
    justify-self: start;
  }
  .knoepfe button {
    border: 0;
    background: none;
    box-shadow: none;
    color: var(--text-dim);
    font: inherit;
    font-size: var(--fs-zeile);
    padding: 0.3rem 0.8rem;
    border-radius: var(--radius-rund);
    cursor: pointer;
    white-space: nowrap;
  }
  .knoepfe button.an {
    background: var(--surface);
    box-shadow: var(--schatten-1);
    color: var(--text);
    font-weight: var(--gw-halb);
  }
  .zeile .folge {
    grid-column: 1 / -1;
    margin: 0;
    font-size: var(--fs-fein);
    color: var(--text-dim);
    line-height: var(--zh-normal);
  }
</style>
